import { Component, inject, OnInit, signal } from '@angular/core';
import { marker as _ } from '@colsen1991/ngx-translate-extract-marker';
import {
  AntragsType,
  ConfigSettingsService,
  Deferred,
  EinreichungComponents,
  MessageBase,
  Stammdaten,
  StammdatenService,
} from '@ebeihilfe/core';
import {
  Antrag,
  EinreichungComponentType,
  EinreichungStep,
  EinreichungStepResultType,
} from '@ebeihilfe/einreichung-old';
import { LegacyAntragsMetadata } from '@ebeihilfe/einreichung-shared/model';
import { ModalService, OverlayService } from '@ebeihilfe/web-logic';

import { AntragModalComponent } from '../antrag-modal/antrag-modal.component';

// eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
@Component({
  selector: 'portal-startup-information',
  templateUrl: './startup-information.component.html',
  styleUrls: ['./startup-information.component.scss'],
  standalone: false,
})
export class StartupInformationComponent implements EinreichungComponentType, OnInit {
  private readonly configService = inject(ConfigSettingsService);
  private readonly message = inject(MessageBase);
  private readonly modals = inject(ModalService);
  private readonly spinnerService = inject(OverlayService);
  private readonly stammdatenService = inject(StammdatenService);

  public antrag: Antrag;
  public component = StartupInformationComponent;
  public isLoading = signal(false);
  public maximumAntragSizeInMb: number;
  public maximumFileSizeInMb: number;
  public name = EinreichungComponents.Startup;
  // Muss nach PopulateUnfinishedAntragComponent (INIT) laufen: next() prüft
  // antrag.hasAntragsMetadata() und braucht dafür den ggf. bereits aus einem
  // unterbrochenen Antrag wiederhergestellten Antrag, nicht den frischen Stub.
  public prio = EinreichungStep.INIT + 1;
  public work: Deferred<EinreichungStepResultType>;

  public async next(): Promise<void> {
    // Verhindert Mehrfachklicks während des laufenden next() (doppelter Spinner/Dialog).
    if (this.isLoading()) {
      return;
    }

    this.isLoading.set(true);
    try {
      // Kurzantrag-Metadaten (Stammdaten-Übernahme per Dialog) werden hier, ohne
      // Step-Wechsel, direkt abgefragt — die Ansicht bleibt dadurch unverändert,
      // bis der Dialog tatsächlich erfolgreich bestätigt wurde.
      if (!this.antrag.hasAntragsMetadata()) {
        await this.prepareKurzantragMetadata();
        return;
      }

      this.work.resolve({
        result: this.antrag,
        shouldProceed: true,
      });
    } finally {
      this.isLoading.set(false);
    }
  }

  public ngOnInit(): void {
    const { constants } = this.configService.appSettings;
    this.maximumFileSizeInMb = constants.maximumFileSizeInMb;
    this.maximumAntragSizeInMb = constants.maximumAntragSizeInMb;
  }

  public canHandle(): boolean {
    return true;
  }

  public destroy(): Promise<void> {
    return Promise.resolve(undefined);
  }

  public handle(
    antrag: Antrag,
    instance: StartupInformationComponent,
    work: Deferred<EinreichungStepResultType>,
  ): Promise<EinreichungStepResultType> {
    instance.antrag = antrag;
    instance.work = work;
    return instance.work;
  }

  public hasUi(): boolean {
    return true;
  }

  private async prepareKurzantragMetadata(): Promise<void> {
    this.spinnerService.show();
    try {
      const stammdaten = await this.stammdatenService.getStammdaten();
      if (!stammdaten) {
        return;
      }

      const kurzAntragData = await this.presentKurzantragDialog(stammdaten);
      if (!kurzAntragData) {
        return;
      }

      this.antrag.setAntragsType(kurzAntragData.type);
      this.antrag.setAntragsData(kurzAntragData.data);

      this.work.resolve({
        result: this.antrag,
        shouldProceed: true,
      });
    } catch (ex) {
      if (ex.message !== 'pin not available') {
        await this.message.showError({
          message: _('view_finished_antrag.text.encryption_error'),
        });
      }
    } finally {
      this.spinnerService.hide();
    }
  }

  private presentKurzantragDialog(
    stammdaten: Stammdaten,
  ): Promise<{ data: LegacyAntragsMetadata[]; type: AntragsType }> {
    return this.modals.openModal<
      AntragModalComponent,
      { data: LegacyAntragsMetadata[]; type: AntragsType },
      Stammdaten
    >(AntragModalComponent, stammdaten);
  }
}
